/* eslint-disable react/no-unescaped-entities */
/* eslint-disable react/jsx-props-no-spreading */
import useGoogleMap from "@/hooks/use-google-hook";
import React, { useCallback, useEffect, useRef } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { GoogleMap, MarkerF } from "@react-google-maps/api";
import { SNAZZY_MAP_STYLES } from "@/constants";
import { MdQuestionMark } from "react-icons/md";
import Swal from "sweetalert2";

// Assuming these are defined elsewhere
const GoogleSearch = ({ onChange, value, isLoaded, id, ...rest }) => {
  const ref = useRef(null);

  const handleScriptLoad = (ref) => {
    const autoComplete = new window.google.maps.places.Autocomplete(
      ref.current,
      {
        types: ["geocode"],
      }
    );
    autoComplete.addListener("place_changed", () =>
      handlePlaceSelect(autoComplete)
    );
  };

  const handlePlaceSelect = (autoComplete) => {
    const addressObject = autoComplete.getPlace();
    const addressComponents = addressObject?.address_components || [];

    const getComponent = (type) =>
      addressComponents.find((component) => component.types.includes(type))
        ?.long_name || "";

    const addressDetails = {
      address: addressObject?.formatted_address || "",
      city: getComponent("locality"),
      state: getComponent("administrative_area_level_1"),
      country: getComponent("country"),
      zipcode: getComponent("postal_code"),
      latitude: addressObject?.geometry?.location?.lat(),
      longitude: addressObject?.geometry?.location?.lng(),
    };

    onChange(addressDetails);
  };

  useEffect(() => {
    if (isLoaded) {
      handleScriptLoad(ref);
      setTimeout(() => {
        if (ref.current) {
          ref.current.setAttribute("autocomplete", "new-password");
        }
      }, 1000);
    }
  }, [isLoaded]);

  return (
    <div className="w-full">
      <input
        id={id ?? "address"}
        className="w-full border border-gray-300 rounded-md px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search for business address..."
        autoComplete="new-password"
        {...rest}
      />
    </div>
  );
};

/**
 * A form field component for handling location-related inputs, including a Google Map.
 * It is styled using Tailwind CSS and manages state with react-hook-form.
 */
const LocationFormFields = () => {
  const {
    control,
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = useFormContext();

  const latitude = watch("latitude");
  const longitude = watch("longitude");
  const { isLoaded, onLoad, onUnmount } = useGoogleMap();
  console.log({ latitude, longitude });
  const formatPrecision = (value, precision = 6) => {
    return parseFloat(parseFloat(value).toFixed(precision));
  };

  const findAddress = useCallback(() => {
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);

    if (isNaN(lat) || isNaN(lng)) {
      console.error("Invalid latitude or longitude");
      return;
    }

    const latLng = new window.google.maps.LatLng(lat, lng);
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === "OK" && results[0]) {
        const addressObject = results[0];
        const addressComponents = addressObject?.address_components || [];

        const getComponent = (type) =>
          addressComponents.find((component) => component.types.includes(type))
            ?.long_name || "";

        setValue("address", addressObject.formatted_address);
        setValue("city", getComponent("locality"));
        setValue("state", getComponent("administrative_area_level_1"));
        setValue("country", getComponent("country"));
        setValue("zipcode", getComponent("postal_code"));

        // Trigger validation for all fields
        trigger(["address", "city", "state", "country", "zipcode"]);
      } else {
        console.error("Geocoder failed due to: ", status);
      }
    });
  }, [latitude, longitude, setValue, trigger]);

  const onDragEnd = (e) => {
    const newLat = e.latLng.lat();
    const newLng = e.latLng.lng();
    console.log("New position:", { lat: newLat, lng: newLng });

    // Update the form values for latitude and longitude
    setValue("latitude", newLat);
    setValue("longitude", newLng);
  };

  return (
    <div className="flex flex-col gap-6">
      <h4 className="mb-0 mt-2 text-2xl font-bold">Location</h4>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Address Search */}
        <div className="md:col-span-2">
          <div className="mb-2">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              <i className="fa fa-search mr-2 text-blue-500" />
              Search Address
              <span className="ml-1 text-red-500">*</span>
            </label>
            <p className="!mb-3 text-sm text-gray-500">
              (Address MUST be TYPED in. Please START TYPING and choose an
              address that Google is suggesting BELOW the address field bar.
              Please do NOT choose an address that your device has saved.)
            </p>
          </div>

          <Controller
            name="address"
            control={control}
            render={({ field: { onChange, value } }) => (
              <div className="flex flex-col gap-1">
                <GoogleSearch
                  isLoaded={isLoaded}
                  onChange={(address) => {
                    if (typeof address === "string") {
                      onChange(address);
                    } else if (address?.address) {
                      // Set all form values from the address object
                      onChange(address.address);
                      setValue("city", address.city, { shouldValidate: true });
                      setValue("state", address.state, {
                        shouldValidate: true,
                      });
                      setValue("country", address.country, {
                        shouldValidate: true,
                      });
                      setValue("zipcode", address.zipcode, {
                        shouldValidate: true,
                      });
                      setValue("latitude", address.latitude, {
                        shouldValidate: true,
                      });
                      setValue("longitude", address.longitude, {
                        shouldValidate: true,
                      });
                    }
                  }}
                  value={value}
                />
                {errors.address && (
                  <span className="mt-2 flex items-center text-sm text-red-500">
                    <i className="fa fa-exclamation-circle mr-1" />
                    {errors.address.message}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        {/* Apartment/Suite */}
        <div>
          <label className="!mb-2 block text-sm font-medium text-gray-700">
            <i className="fa fa-home mr-2 text-blue-500" />
            Apartment/Suite (optional)
          </label>
          <Controller
            name="apt_suite"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <input
                  {...field}
                  type="text"
                  className="w-full rounded-md border border-gray-300 px-4 py-3 "
                  placeholder="e.g. Suite 405"
                />
              </div>
            )}
          />
        </div>

        {/* City */}
        <div>
          <label className="!mb-2 block text-sm font-medium text-gray-700">
            <i className="fa fa-city mr-2 text-blue-500" />
            City
            <span className="ml-1 text-red-500">*</span>
          </label>
          <Controller
            name="city"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <input
                  {...field}
                  type="text"
                  className="w-full rounded-md border border-gray-300 px-4 py-3 "
                  placeholder="e.g. New York"
                />
                {errors.city && (
                  <span className="mt-2 flex items-center text-sm text-red-500">
                    <i className="fa fa-exclamation-circle mr-1" />
                    {errors.city.message}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        {/* State */}
        <div>
          <label className="!mb-2 block text-sm font-medium text-gray-700">
            <i className="fa fa-flag mr-2 text-blue-500" />
            State
            <span className="ml-1 text-red-500">*</span>
          </label>
          <Controller
            name="state"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <input
                  {...field}
                  type="text"
                  className="w-full rounded-md border border-gray-300 px-4 py-3 "
                  placeholder="e.g. New York"
                />
                {errors.state && (
                  <span className="mt-2 flex items-center text-sm text-red-500">
                    <i className="fa fa-exclamation-circle mr-1" />
                    {errors.state.message}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        {/* ZIP / Postal Code */}
        <div>
          <label className="!mb-2 block text-sm font-medium text-gray-700">
            <i className="fa fa-envelope mr-2 text-blue-500" />
            ZIP / Postal Code
            <span className="ml-1 text-red-500">*</span>
          </label>
          <Controller
            name="zipcode"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <input
                  {...field}
                  onChange={(e) => {
                    setValue("zipcode", e.target.value);
                    trigger("zipcode");
                  }}
                  type="text"
                  className="w-full rounded-md border border-gray-300 px-4 py-3 "
                  placeholder="e.g. 10001"
                />
                {errors.zipcode && (
                  <span className="mt-2 flex items-center text-sm text-red-500">
                    <i className="fa fa-exclamation-circle mr-1" />
                    {errors.zipcode.message}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        {/* Area */}
        <div>
          <label className="!mb-2 block text-sm font-medium text-gray-700">
            <i className="fa fa-map mr-2 text-blue-500" />
            Area (optional)
          </label>
          <Controller
            name="area"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <input
                  {...field}
                  type="text"
                  className="w-full rounded-md border border-gray-300 px-4 py-3 "
                  placeholder="e.g. Manhattan"
                />
              </div>
            )}
          />
        </div>

        {/* Country */}
        <div>
          <label className="!mb-2 block text-sm font-medium text-gray-700">
            <i className="fa fa-globe mr-2 text-blue-500" />
            Country
            <span className="ml-1 text-red-500">*</span>
          </label>
          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <input
                  {...field}
                  type="text"
                  className="w-full rounded-md border border-gray-300 px-4 py-3 "
                  placeholder="e.g. United States"
                />
                {errors.country && (
                  <span className="mt-2 flex items-center text-sm text-red-500">
                    <i className="fa fa-exclamation-circle mr-1" />
                    {errors.country.message}
                  </span>
                )}
              </div>
            )}
          />
        </div>
      </div>

      {/* Map Section */}
      <div className="mt-6">
        <div className="!mb-4">
          <label className="mb-2 block text-sm font-medium text-gray-700">
            <i className="fa fa-map-pin mr-2 text-blue-500" />
            Location Map
          </label>
          <p className="!mb-0 text-sm text-gray-500">
            Drag the marker to adjust your precise location. Coordinates will
            update automatically.
          </p>
        </div>

        <div className="h-[400px] w-full overflow-hidden rounded-lg border border-gray-300 shadow-sm">
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={{ lat: +latitude || 39.5, lng: +longitude || -100.0 }}
            zoom={+latitude && +longitude ? 15 : 3.6}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={{
              styles: SNAZZY_MAP_STYLES,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
            }}
          >
            <MarkerF
              draggable
              onDragEnd={onDragEnd}
              position={{ lat: +latitude || 39.5, lng: +longitude || -100.0 }}
              icon={{
                url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
                scaledSize: new window.google.maps.Size(40, 40),
              }}
            />
          </GoogleMap>
        </div>
      </div>

      {/* Coordinates Section */}
      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3 hidden">
        {/* Latitude */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            <i className="fa fa-latitude mr-2 text-blue-500" />
            Latitude
            <span className="ml-1 text-red-500">*</span>
          </label>
          <Controller
            name="latitude"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <input
                  {...field}
                  type="number"
                  min="-90"
                  max="90"
                  step="any"
                  className="w-full rounded-md border border-gray-300 px-4 py-3 "
                  placeholder="Latitude"
                />
                {errors.latitude && (
                  <span className="mt-2 flex items-center text-sm text-red-500">
                    <i className="fa fa-exclamation-circle mr-1" />
                    {errors.latitude.message}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        {/* Longitude */}
        <div>
          <label className="mb-2 flex  text-sm font-medium text-gray-700">
            <i className="fa fa-longitude mr-2 text-red-500" />
            Longitude
            <span className="ml-1 text-red-500">*</span>
            <MdQuestionMark
              onClick={() =>
                Swal.fire({
                  text: "Listing type",
                  icon: "question",
                })
              }
              style={{
                color: "#fff",
                background: "#8ec639",
                borderRadius: "50%",
                fontWeight: "bold",
                fontSize: "1.2rem",
                marginLeft: "8px",
                boxShadow: "0 0 8px 2px #8ec639, 0 0 0 3px #c6f6d5",
                border: "2px solid #8ec639",
                cursor: "pointer",
                padding: "2px",
                animation: "pulse-glow 1.2s infinite alternate",
              }}
              className="focus:outline-none focus:ring-2 focus:ring-[#8ec639]"
              title="Please don’t touch this section. Google auto populates this for you when you enter your correct address above."
            />
          </label>
          <Controller
            name="longitude"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <input
                  {...field}
                  type="number"
                  min="-180"
                  max="180"
                  step="any"
                  className="w-full rounded-md border border-gray-300 px-4 py-3 focus:border-red-500 "
                  placeholder="Longitude"
                />
                {errors.longitude && (
                  <span className="mt-2 flex items-center text-sm text-red-500">
                    <i className="fa fa-exclamation-circle mr-1" />
                    {errors.longitude.message}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        {/* Find Address Button */}
        <div className="flex items-end">
          <button
            type="button"
            onClick={findAddress}
            className="h-[50px] w-full rounded-md bg-gradient-to-r from-red-600 to-red-700 px-4 py-3 font-medium text-white shadow-md transition-all duration-300 hover:from-red-700 hover:to-red-800"
          >
            <i className="fa fa-map-marked-alt mr-2" />
            Find Address
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationFormFields;
